import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import '../styles/Employees.css'; // Reusing the same styles
import moment from 'moment';

const Requests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [activeTab, setActiveTab] = useState('requests');
    const [processingRequest, setProcessingRequest] = useState(null);
    const [requestSearchTerm, setRequestSearchTerm] = useState('');
    const navigate = useNavigate();

    // Add dark theme by default
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'dark');
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [requestsRes, companyRes] = await Promise.all([
                    api.get('/api/requests/company'), // Changed from '/api/requests' to '/api/requests/company'
                    api.get('/api/companies/info')
                ]);

                console.log('Requests API response:', requestsRes.data);
                
                // Ensure requests is always an array
                const requestsData = Array.isArray(requestsRes.data) ? requestsRes.data : [];
                console.log('Processed requests data:', requestsData);
                
                setRequests(requestsData);
                setCompanyInfo(companyRes.data);
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch requests:', err);
                console.error('Error details:', err.response?.data);
                // Set empty array on error to prevent map error
                setRequests([]);
                setError('Failed to fetch requests');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleRequestAction = async (requestId, action) => {
        setProcessingRequest(requestId);
        try {
            await api.put(`/api/requests/${requestId}/status`, {
                status: action,
                manager_notes: `Request ${action} by manager`
            });

            // Update the requests list to reflect the change
            setRequests(prevRequests => 
                prevRequests.map(request => 
                    request.id === requestId 
                        ? { ...request, status: action }
                        : request
                )
            );

            console.log(`Request ${requestId} ${action} successfully`);
        } catch (err) {
            console.error(`Failed to ${action} request:`, err);
            alert(`Failed to ${action} request. Please try again.`);
        } finally {
            setProcessingRequest(null);
        }
    };

    if (loading) {
        return <div className="employees-page loading">Loading...</div>;
    }

    if (error) {
        return <div className="employees-page error">Error: {error}</div>;
    }

    const handleNavigation = (route) => {
        navigate(route);
    };

    // Add function to handle view toggle
    const handleViewToggle = () => {
        // Get the token from localStorage
        const token = localStorage.getItem('token') || localStorage.getItem('employeeToken');
        
        if (token) {
            // Store the token in both localStorage and sessionStorage for the employee app
            localStorage.setItem('employeeToken', token);
            sessionStorage.setItem('employeeToken', token);
            
            // Navigate to the employee calendar
            window.location.href = 'https://calendar.joinoptivo.com';
        } else {
            console.error('No token found for navigation');
        }
    };

    const handleTabClick = (tab, event) => {
        if (event) {
            event.preventDefault();
        }
        setActiveTab(tab);
        if (tab === 'dashboard') {
            navigate('/dashboard');
        } else if (tab === 'employees') {
            navigate('/employees');
        }
    };

    // Filter requests based on search term
    const filteredRequests = requests.filter(request => {
        if (!requestSearchTerm.trim()) return true;
        
        const searchLower = requestSearchTerm.toLowerCase();
        const firstName = request.first_name?.toLowerCase() || '';
        const lastName = request.last_name?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const reason = request.reason?.toLowerCase() || '';
        const requestType = request.request_type?.toLowerCase().replace('_', ' ') || '';
        const status = request.status?.toLowerCase() || '';
        
        return firstName.includes(searchLower) ||
               lastName.includes(searchLower) ||
               fullName.includes(searchLower) ||
               reason.includes(searchLower) ||
               requestType.includes(searchLower) ||
               status.includes(searchLower);
    });

    return (
        <div className="dashboard">
            <div className="top-header" style={{
                left: '0',  // Override the CSS that sets left: 300px
                paddingLeft: '24px'  // Add padding to match the original design
            }}>
                <h1 style={{ 
                    color: 'white', 
                    fontFamily: 'Albert Sans, sans-serif',
                    fontWeight: '700',
                    fontSize: '48px',
                    margin: '0',
                    position: 'absolute',
                    left: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 100
                }}>{companyInfo?.name || 'Loading...'}</h1>
                
                <div style={{ 
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '24px'
                }}>
                    {/* View Toggle Switch replaces navigation */}
                    <div style={{
                        width: '200px',
                        height: '44px',
                        backgroundColor: '#2D2D2D',
                        borderRadius: '22px',
                        padding: '3px',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                    }} onClick={handleViewToggle}>
                        <div style={{
                            position: 'absolute',
                            top: '3px',
                            left: '3px',
                            width: 'calc(50% - 3px)',
                            height: '38px',
                            backgroundColor: '#353535',
                            borderRadius: '19px',
                            transition: 'all 0.3s ease',
                            zIndex: 1
                        }}></div>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            height: '38px',
                            width: '100%',
                            zIndex: 2
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Calendar</div>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Manager</div>
                        </div>
                    </div>
                </div>

                {/* Task Search Box - Far Right Corner */}
                <div className="task-search-box" style={{ 
                    position: 'absolute',
                    right: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '250px',
                    display: 'flex',
                    alignItems: 'center',
                    background: 'var(--bg-secondary)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    zIndex: 2
                }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                        <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input 
                        type="text" 
                        placeholder="Search Requests" 
                        style={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif'
                        }}
                        value={requestSearchTerm}
                        onChange={(e) => setRequestSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="company-info" style={{
                position: 'absolute',
                top: '120px',
                left: '24px',
                zIndex: 10
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: 'auto', 
                    position: 'relative' 
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        marginLeft: '0'
                    }}>
                        <div className="sub-nav-menu" style={{ marginLeft: '0', display: 'flex', alignItems: 'center' }}>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('dashboard', event)}
                                style={{
                                    color: activeTab === 'dashboard' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginRight: '32px',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Dashboard
                            </a>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'employees' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('employees', event)}
                                style={{
                                    color: activeTab === 'employees' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginRight: '32px',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Employees
                            </a>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'requests' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('requests', event)}
                                style={{
                                    color: activeTab === 'requests' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginRight: '32px',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Requests
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="dashboard-content">
                <div className="calendar-section">
                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
                        gap: '24px',
                        width: '100%',
                        maxWidth: 'none',
                        margin: '0',
                        padding: '0 40px'
                    }}>
                        {filteredRequests.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: 'var(--text-secondary)',
                                fontSize: '16px'
                            }}>
                                No requests found.
                            </div>
                        ) : (
                            filteredRequests.map(request => (
                                    <div key={request.id} style={{ 
                                    backgroundColor: request.status === 'approved' ? '#10B981' : 
                                                   request.status === 'denied' ? '#EF4444' : '#6B7280',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    border: `1px solid ${request.status === 'approved' ? '#059669' : 
                                                        request.status === 'denied' ? '#DC2626' : '#4B5563'}`,
                                    transition: 'all 0.3s ease'
                                }}>
                                    {/* Header Bar - changes color based on status */}
                                    <div style={{
                                        backgroundColor: request.status === 'approved' ? '#059669' : 
                                                       request.status === 'denied' ? '#DC2626' : '#F59E0B',
                                        height: '8px',
                                        width: '100%',
                                        transition: 'all 0.3s ease'
                                    }}></div>
                                    
                                    {/* Card Content */}
                                    <div style={{
                                        padding: '24px'
                                    }}>
                                        {/* Employee Name */}
                                        <h3 style={{
                                            color: 'white',
                                            fontSize: '28px',
                                            fontWeight: '600',
                                            margin: '0 0 8px 0',
                                            textAlign: 'center'
                                        }}>
                                            {request.first_name} {request.last_name}
                                        </h3>
                                        
                                        {/* Request Type and Dates */}
                                        <div style={{
                                            textAlign: 'center',
                                            marginBottom: '16px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                marginBottom: '4px'
                                            }}>
                                                <p style={{
                                                    color: '#D1D5DB',
                                                    fontSize: '18px',
                                                    margin: '0',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {request.request_type.replace('_', ' ')} - {moment(request.start_date).format('MMMM Do')} to {moment(request.end_date).format('Do')}
                                                </p>
                                                {request.is_pto && (
                                                    <span style={{
                                                        backgroundColor: '#10B981',
                                                        color: 'white',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        PTO
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Reason */}
                                        <div style={{
                                            textAlign: 'center',
                                            marginBottom: '24px'
                                        }}>
                                            <p style={{
                                                color: 'white',
                                                fontSize: '20px',
                                                margin: '0',
                                                fontStyle: 'italic'
                                            }}>
                                                "{request.reason}"
                                            </p>
                                        </div>
                                        
                                        {/* Action Buttons or Status */}
                                        {request.status === 'pending' ? (
                                            <div style={{
                                                display: 'flex',
                                                gap: '16px',
                                                justifyContent: 'center'
                                            }}>
                                                <button
                                                    onClick={() => handleRequestAction(request.id, 'approved')}
                                                    disabled={processingRequest === request.id}
                                                    style={{
                                                        backgroundColor: '#10B981',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        padding: '12px 32px',
                                                        fontSize: '18px',
                                                        fontWeight: '600',
                                                        cursor: processingRequest === request.id ? 'not-allowed' : 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        minWidth: '120px',
                                                        opacity: processingRequest === request.id ? 0.7 : 1
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (processingRequest !== request.id) {
                                                            e.target.style.backgroundColor = '#059669';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (processingRequest !== request.id) {
                                                            e.target.style.backgroundColor = '#10B981';
                                                        }
                                                    }}
                                                >
                                                    {processingRequest === request.id ? 'Processing...' : 'Approve'}
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleRequestAction(request.id, 'denied')}
                                                    disabled={processingRequest === request.id}
                                                    style={{
                                                        backgroundColor: '#EF4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        padding: '12px 32px',
                                                        fontSize: '18px',
                                                        fontWeight: '600',
                                                        cursor: processingRequest === request.id ? 'not-allowed' : 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        minWidth: '120px',
                                                        opacity: processingRequest === request.id ? 0.7 : 1
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (processingRequest !== request.id) {
                                                            e.target.style.backgroundColor = '#DC2626';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (processingRequest !== request.id) {
                                                            e.target.style.backgroundColor = '#EF4444';
                                                        }
                                                    }}
                                                >
                                                    {processingRequest === request.id ? 'Processing...' : 'Deny'}
                                                </button>
                                            </div>
                                        ) : (
                                            /* Show status when approved/denied */
                                            <div style={{
                                                textAlign: 'center'
                                            }}>
                                                <div style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                    padding: '12px 24px',
                                                    borderRadius: '8px',
                                                    fontWeight: '600',
                                                    fontSize: '18px',
                                                    color: 'white'
                                                }}>
                                                    {request.status === 'approved' ? (
                                                        <>
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <polyline points="20,6 9,17 4,12"/>
                                                            </svg>
                                                            APPROVED
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="18" y1="6" x2="6" y2="18"/>
                                                                <line x1="6" y1="6" x2="18" y2="18"/>
                                                            </svg>
                                                            DENIED
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                            </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Requests; 